import api from './api';
import type { ContractorJob, Property } from '../types/contractorJob';

// Re-export types for convenience
export type { ContractorJob, Property } from '../types/contractorJob';

// Validate retrieve code and get contractor information
export const validateRetrieveCode = async (retrieveCode: string): Promise<any> => {
  const response = await api.post('/jobs/validate-retrieve-code', {
    retrieveCode
  });
  return response.data;
};

// Agency-side functions (require authentication)

export const assignContractor = async (
  agencySlug: string,
  propertyId: string,
  data: {
    contractorType: 'photographer' | 'styler';
    contractorId: string;
    dueDate?: string;
  }
): Promise<ContractorJob> => {
  const response = await api.post(
    `/agencies/${agencySlug}/properties/${propertyId}/assign-contractor`,
    data
  );
  return response.data;
};

export const getPropertyJobs = async (
  agencySlug: string,
  propertyId: string
): Promise<ContractorJob[]> => {
  const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/jobs`);
  return response.data;
};

export const completeJob = async (
  agencySlug: string,
  jobId: string
): Promise<ContractorJob> => {
  const response = await api.put(`/agencies/${agencySlug}/jobs/${jobId}/complete`);
  return response.data;
};

export const cancelJob = async (
  agencySlug: string,
  jobId: string
): Promise<{ message: string; job: ContractorJob }> => {
  const response = await api.delete(`/agencies/${agencySlug}/jobs/${jobId}/cancel`);
  return response.data;
};

// Update job details (agency side)
export const updateAgencyJob = async (
  agencySlug: string,
  jobId: string,
  payload: { contractorType?: string; notes?: string; startDate?: string; finishDate?: string }
): Promise<ContractorJob> => {
  const response = await api.patch(`/agencies/${agencySlug}/jobs/${jobId}`, payload);
  return response.data;
};

// Quote lifecycle - agency side
export const agencySendQuote = async (
  agencySlug: string,
  jobId: string,
  payload: { amount: number; notes?: string; currency?: string }
) => {
  const res = await api.post(`/agencies/${agencySlug}/jobs/${jobId}/quote/send`, payload);
  return res.data;
};

export const agencyCounterQuote = async (
  agencySlug: string,
  jobId: string,
  payload: { amount: number; notes?: string; currency?: string }
) => {
  const res = await api.post(`/agencies/${agencySlug}/jobs/${jobId}/quote/counter`, payload);
  return res.data;
};

export const agencyAcceptQuote = async (
  agencySlug: string,
  jobId: string
) => {
  const res = await api.post(`/agencies/${agencySlug}/jobs/${jobId}/quote/accept`);
  return res.data;
};

export const agencyDeclineQuote = async (
  agencySlug: string,
  jobId: string,
  notes?: string
) => {
  const res = await api.post(`/agencies/${agencySlug}/jobs/${jobId}/quote/decline`, { notes });
  return res.data;
};

// Quote lifecycle - contractor side
export const contractorRespondQuote = async (
  token: string,
  payload: { amount: number; notes?: string; gstInclusive?: boolean }
) => {
  const res = await api.post(`/jobs/${token}/quote/respond`, payload);
  return res.data;
};

export const addJobMessageFromAgency = async (
  agencySlug: string,
  jobId: string,
  text: string,
  senderName?: string
): Promise<ContractorJob> => {
  const response = await api.post(`/agencies/${agencySlug}/jobs/${jobId}/messages`, {
    text,
    isFromContractor: false,
    senderName,
  });
  return response.data;
};

// Public functions (no authentication required, accessed via token)

export const getJobByToken = async (token: string): Promise<{
  job: ContractorJob;
  property: Property;
  actionsRemaining: number;
  hasSubscription: boolean;
  actionCount: number;
}> => {
  const response = await api.get(`/jobs/${token}`);
  return response.data;
};

// Get quote details (tracks quote view for free users)
export const getQuoteDetails = async (token: string): Promise<{
  canViewQuote: boolean;
  quoteViewsRemaining: number;
  agencyQuote: any;
  contractorQuote: any;
  quoteStatus: string;
}> => {
  const response = await api.get(`/jobs/${token}/quote/details`);
  return response.data;
};

export const updateJobStatus = async (
  token: string,
  status: 'pending' | 'in_progress' | 'completed'
): Promise<ContractorJob> => {
  const response = await api.put(`/jobs/${token}/status`, { status });
  return response.data;
};

export const addJobMessage = async (
  token: string,
  text: string,
  senderName?: string
): Promise<ContractorJob> => {
  const response = await api.post(`/jobs/${token}/messages`, {
    text,
    isFromContractor: true,
    senderName,
  });
  return response.data;
};

export const uploadJobPhoto = async (
  token: string,
  file: File
): Promise<{ url: string; key: string; uploadUrl: string }> => {
  // First, get presigned URL for upload
  const response = await api.post(`/jobs/${token}/upload-photo`, {
    fileName: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
  });
  
  const { uploadUrl, key } = response.data;
  
  // Upload file directly to storage
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });
  
  if (!uploadResponse.ok) {
    throw new Error('Failed to upload file to storage');
  }
  
  // Finalize the upload
  await api.post(`/jobs/${token}/upload-photo/finalize`, {
    key,
    sizeBytes: file.size,
  });
  
  return response.data;
};


export const uploadFloorPlan = async (token: string, file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post(`/jobs/${token}/upload-floor-plan`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadInvoice = async (
  token: string,
  file: File,
  amount?: number,
  notes?: string
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  if (amount !== undefined) formData.append('amount', amount.toString());
  if (notes) formData.append('notes', notes);
  
  const response = await api.post(`/jobs/${token}/upload-invoice`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadAttachment = async (token: string, file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post(`/jobs/${token}/upload-attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteJob = async (agencySlug: string, jobId: string): Promise<any> => {
  const response = await api.delete(`/agencies/${agencySlug}/jobs/${jobId}`);
  return response.data;
};


